import { createRouter, createWebHistory, type RouteRecordRaw, type RouteLocationNormalized, type NavigationGuardNext } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions'

// Import views
import Login from '@/views/auth/Login.vue'
import OldLogin from '@/views/auth/OldLogin.vue'
import Register from '@/views/auth/Register.vue'
import ForgotPassword from '@/views/auth/ForgotPassword.vue'
import ResetPassword from '@/views/auth/ResetPassword.vue'
import Dashboard from '@/views/dashboard/Dashboard.vue'
import AssignedToMe from '@/views/assigned-to-me/AssignedToMe.vue'
import ProjectDetail from '@/views/projects/ProjectDetail.vue'
import TestRuns from '@/views/test-runs/TestRuns.vue'
import TestRunDetail from '@/views/test-runs/TestRunDetail.vue'
import TestCases from '@/views/test-cases/TestCases.vue'
import TestCaseDetail from '@/views/test-cases/TestCaseDetail.vue'
import Bugs from '@/views/bugs/Bugs.vue'
import BugDetail from '@/views/bugs/BugDetail.vue'
import Reports from '@/views/reports/Reports.vue'
import Profile from '@/views/profile/Profile.vue'
import Notifications from '@/views/notifications/Notifications.vue'
import CrossTeamSanity from '@/views/cross-team-sanity/CrossTeamSanity.vue'
import NotFound from '@/views/errors/NotFound.vue'

// Extend route meta interface
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresGuest?: boolean
    requiresPermission?: string
    requiresTeamOrProjectPermission?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  // Authentication routes
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresGuest: true }
  },
  {
    path: '/old-login',
    name: 'OldLogin',
    component: OldLogin,
    meta: { requiresGuest: true }
  },
  {
    path: '/create-new-boss',
    name: 'Register',
    component: Register,
    meta: { requiresGuest: true }
  },
  {
    path: '/register',
    redirect: '/login'
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { requiresGuest: true }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresGuest: true }
  },
  
  // Main application routes
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true }
  },
  {
    path: '/assigned-to-me',
    name: 'AssignedToMe',
    component: AssignedToMe,
    meta: { requiresAuth: true }
  },
  {
    path: '/cross-team-sanity',
    name: 'CrossTeamSanity',
    component: CrossTeamSanity,
    meta: { requiresAuth: true }
  },
  {
    path: '/cross-team-sanity/:id',
    name: 'CrossTeamSanityDetail',
    component: () => import('@/views/cross-team-sanity/CrossTeamSanityDetail.vue'),
    meta: { requiresAuth: true }
  },
  
  // Project routes
  {
    path: '/projects/:projectId',
    name: 'ProjectDetail',
    component: ProjectDetail,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'ProjectOverview',
        component: () => import('@/views/projects/ProjectOverview.vue')
      },
      {
        path: 'test-runs',
        name: 'ProjectTestRuns',
        component: () => import('@/views/projects/ProjectTestRuns.vue')
      },
      {
        path: 'test-cases',
        name: 'ProjectTestCases',
        component: () => import('@/views/projects/ProjectTestCases.vue')
      },
      {
        path: 'bugs',
        name: 'ProjectBugs',
        component: () => import('@/views/projects/ProjectBugs.vue')
      },
      {
        path: 'features',
        name: 'ProjectFeatures',
        component: () => import('@/views/projects/ProjectFeatures.vue')
      },
      {
        path: 'settings',
        name: 'ProjectSettings',
        component: () => import('@/views/projects/ProjectSettings.vue'),
        meta: { requiresPermission: 'viewSettings' }
      }
    ]
  },
  
  // Test Runs routes
  {
    path: '/test-runs',
    name: 'TestRuns',
    component: TestRuns,
    meta: { requiresAuth: true }
  },
  {
    path: '/test-runs/:runId',
    name: 'TestRunDetail',
    component: TestRunDetail,
    meta: { requiresAuth: true }
  },
  {
    path: '/projects/:projectId/test-runs/:runId',
    name: 'ProjectTestRunDetail',
    component: TestRunDetail,
    meta: { requiresAuth: true }
  },
  {
    path: '/projects/:projectId/test-runs/create',
    name: 'CreateTestRun',
    component: () => import('@/views/test-runs/CreateTestRun.vue'),
    meta: { requiresAuth: true, requiresPermission: 'createTestRuns' }
  },
  {
    path: '/projects/:projectId/test-runs/:runId/edit',
    name: 'EditTestRun',
    component: () => import('@/views/test-runs/EditTestRun.vue'),
    meta: { requiresAuth: true, requiresPermission: 'editTestRuns' }
  },
  {
    path: '/test-runs/:runId/edit',
    name: 'EditTestRunGeneral',
    component: () => import('@/views/test-runs/EditTestRun.vue'),
    meta: { requiresAuth: true }
  },
  
  // Feature routes
  {
    path: '/projects/:projectId/features/create',
    name: 'CreateFeature',
    component: () => import('@/views/features/CreateFeature.vue'),
    meta: { requiresAuth: true, requiresPermission: 'createFeatures' }
  },
  {
    path: '/projects/:projectId/features/:featureId',
    name: 'FeatureDetail',
    component: () => import('@/views/features/FeatureDetail.vue'),
    meta: { requiresAuth: true }
  },
  
  // Test Cases routes
  {
    path: '/projects/:projectId/test-cases',
    name: 'TestCases',
    component: TestCases,
    meta: { requiresAuth: true }
  },
  {
    path: '/projects/:projectId/test-cases/create',
    name: 'CreateTestCase',
    component: () => import('@/views/test-cases/CreateTestCase.vue'),
    meta: { requiresAuth: true, requiresPermission: 'createTestCases' }
  },
  {
    path: '/projects/:projectId/test-cases/:caseId',
    name: 'TestCaseDetail',
    component: TestCaseDetail,
    meta: { requiresAuth: true }
  },
  
  // Bugs routes
  {
    path: '/bugs',
    name: 'Bugs',
    component: Bugs,
    meta: { requiresAuth: true }
  },
  {
    path: '/bugs/:bugId',
    name: 'BugDetail',
    component: BugDetail,
    meta: { requiresAuth: true }
  },
  {
    path: '/projects/:projectId/bugs/create',
    name: 'CreateBug',
    component: () => import('@/views/bugs/CreateBug.vue'),
    meta: { requiresAuth: true, requiresPermission: 'createBugs' }
  },
  {
    path: '/projects/:projectId/bugs/:bugId',
    name: 'ProjectBugDetail',
    component: BugDetail,
    meta: { requiresAuth: true }
  },
  
  // Reports routes
  {
    path: '/reports',
    name: 'Reports',
    component: Reports,
    meta: { requiresAuth: true }
  },
  
  // Profile route
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: { requiresAuth: true }
  },
  
  // Teams route
  {
    path: '/teams',
    name: 'Teams',
    component: () => import('@/views/teams/Teams.vue'),
    meta: { 
      requiresAuth: true,
      requiresTeamOrProjectPermission: true
    }
  },
  
  // Team Details route
  {
    path: '/teams/:id',
    name: 'TeamDetails',
    component: () => import('@/views/teams/TeamDetails.vue'),
    meta: { 
      requiresAuth: true,
      requiresTeamOrProjectPermission: true
    }
  },
  
  // Users Management route
  {
    path: '/users',
    name: 'Users',
    component: () => import('@/views/users/Users.vue'),
    meta: { requiresAuth: true, requiresPermission: 'viewUsers' }
  },
  
  // Using MongoDB database only

  // Notifications route
  {
    path: '/notifications',
    name: 'Notifications',
    component: Notifications,
    meta: { requiresAuth: true }
  },
  
  // 404 route
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guards
router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  // Note: to and from parameters are used below in conditional checks
  const authStore = useAuthStore()
  
  // Initialize auth if not already initialized (ensures token is set for API calls)
  if (!authStore.user && !authStore.loading) {
    await authStore.initializeAuth()
  }
  
  // Wait for auth to initialize if it's loading
  if (authStore.loading) {
    await new Promise<void>(resolve => {
      const unwatch = authStore.$subscribe(() => {
        if (!authStore.loading) {
          unwatch()
          resolve()
        }
      })
    })
  }
  
  // Ensure token is set on HTTP adapter even if user is already authenticated
  // This handles navigation after login when user is set but token might not be on adapter
  if (authStore.isAuthenticated && !authStore.loading) {
    const { getAccessToken } = await import('@/utils/httpClient')
    const token = getAccessToken()
    if (token) {
      const { db } = await import('@/database')
      db.setAuthToken(token)
    }
  }
  
  // No special handling needed for root path since it redirects to /dashboard
  
  // Check authentication requirements
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // Store the intended URL for redirect after login
    if (to.fullPath && to.fullPath !== '/login' && to.fullPath !== '/') {
      localStorage.setItem('redirect_after_login', to.fullPath)
    }
    next('/login')
    return
  }
  
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    next('/dashboard')
    return
  }
  
  // Check permission requirements for authenticated users
  if (to.meta.requiresPermission && authStore.isAuthenticated) {
    const { can } = usePermissions()
    const permissionMethod = to.meta.requiresPermission as keyof typeof can
    
    // Check if the permission method exists and user has permission
    if (typeof can[permissionMethod] === 'function' && !can[permissionMethod]()) {
      
      // Show error notification
      const { useToast } = await import('@/composables/useToast')
      const { error } = useToast()
      error('You do not have permission to access this page')
      
      // Redirect to previous page or dashboard
      if (from.path && from.path !== '/' && from.path !== '/dashboard') {
        next(from.path)
      } else {
        next('/dashboard')
      }
      return
    }
  }
  
  // Check team/project creation permission for teams routes
  if (to.meta.requiresTeamOrProjectPermission && authStore.isAuthenticated) {
    const { can } = usePermissions()
    
    // User must have either createTeams OR createProjects permission
    if (!can.createTeams() && !can.createProjects()) {
      
      // Show error notification
      const { useToast } = await import('@/composables/useToast')
      const { error } = useToast()
      error('You do not have permission to access Teams & Projects management')
      
      // Redirect to dashboard
      next('/dashboard')
      return
    }
  }
  
  next()
})

// Set default page titles based on route
router.afterEach((to) => {
  // Import setPageTitle dynamically to avoid circular dependencies
  import('@/composables/usePageTitle').then(({ setPageTitle }) => {
    // Route name to title mapping
    const routeTitleMap: Record<string, string> = {
      'Dashboard': 'Dashboard',
      'Login': 'Login',
      'Register': 'Register',
      'ForgotPassword': 'Forgot Password',
      'ResetPassword': 'Reset Password',
      'AssignedToMe': 'Assigned to Me',
      'ProjectDetail': 'Project',
      'ProjectOverview': 'Project Overview',
      'ProjectTestRuns': 'Test Runs',
      'ProjectTestCases': 'Test Cases',
      'ProjectBugs': 'Bugs',
      'ProjectFeatures': 'Features',
      'ProjectSettings': 'Project Settings',
      'TestRuns': 'Test Runs',
      'TestRunDetail': 'Test Run',
      'ProjectTestRunDetail': 'Test Run',
      'CreateTestRun': 'Create Test Run',
      'EditTestRun': 'Edit Test Run',
      'EditTestRunGeneral': 'Edit Test Run',
      'TestCases': 'Test Cases',
      'TestCaseDetail': 'Test Case',
      'CreateTestCase': 'Create Test Case',
      'Bugs': 'Bugs',
      'BugDetail': 'Bug',
      'ProjectBugDetail': 'Bug',
      'CreateBug': 'Create Bug',
      'FeatureDetail': 'Feature',
      'CreateFeature': 'Create Feature',
      'Reports': 'Reports',
      'Profile': 'Profile',
      'Teams': 'Teams',
      'TeamDetails': 'Team Details',
      'Users': 'Users',
      'Notifications': 'Notifications',
      'CrossTeamSanity': 'Cross Team Sanity',
      'NotFound': 'Page Not Found'
    }
    
    // Get title from route name, or use empty string (will default to "Kaizen Platform")
    const routeTitle = routeTitleMap[to.name as string] || ''
    
    // Only set default title if it's not a detail page (detail pages will set their own)
    // Detail pages typically have dynamic params like :id, :runId, :bugId, :featureId, :caseId
    const isDetailPage = to.params.runId || to.params.bugId || to.params.featureId || 
                         to.params.caseId || to.params.id || to.params.projectId
    
    // For detail pages, don't set a default title - let the component set it
    // For list pages and other pages, set the default title (empty will show "Kaizen Platform")
    if (!isDetailPage || to.name === 'ProjectDetail') {
      setPageTitle(routeTitle)
    }
  })
})

export default router

